import { Fragment, memo, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { Editor, EditorState, convertFromRaw } from "draft-js";
import { segmentSeq, segmentText, segmentsSentenceCount, sentenceText, turnAtSentence } from "../vendor/liveTapRenderer";
import SlideDown from "./SlideDown";
import { toTitleCase } from "../services/utilities";
import s from "./LiveTapInfoPanel.module.scss";

/**
 * Live Tap info panel (TT-972) — the symbol description + transcript views the
 * tapestry detail page can summon, brought to the live tapestry page.
 *
 * Two differences from DetailPage's version, both forced by the source data:
 * the transcript is built from the live feed's speaker turns rather than a
 * finished document's text, and it follows the playhead instead of a phrase
 * search. The description is the same draft-js raw JSON taptap authors, read
 * through the same read-only Editor.
 *
 * `focus` (TT-1174 note 5) is a sentence the host asked to see — a quote
 * clicked on the scene board — by its ordinal in the transcript. The panel
 * scrolls to its turn, marks the sentence, and holds there while the
 * playhead moves on (the accent rail keeps showing the live turn); "Back to
 * live" (`onRelease`) lets go.
 */

function LiveTapInfoPanel({ symbolName, description, segments = [], currentTurn, focus = null, onRelease }) {
  const scrollerRef = useRef();
  const targetRef = useRef();

  // The raw JSON is what the backend stores; a description that fails to parse
  // (hand-edited, or a legacy plain-text one) shouldn't take the panel down with
  // it — we just show nothing for it. An EMPTY draft is also nothing: taptap
  // seeds every symbol with `convertToRaw(EditorState.createEmpty())`, which
  // parses fine and would otherwise render a labelled section with no text in it.
  const editorState = useMemo(() => {
    if (!description) return null;
    try {
      const state = EditorState.createWithContent(convertFromRaw(JSON.parse(description)));
      return state.getCurrentContent().hasText() ? state : null;
    } catch {
      return null;
    }
  }, [description]);

  // Join each turn's sentences once per feed update rather than once per render.
  const turnTexts = useMemo(() => segments.map(segmentText), [segments]);

  // The focused sentence's turn and its first sentence's ordinal.
  const held = focus != null;
  const focusTurn = useMemo(() => (held ? turnAtSentence(segments, focus) : null), [segments, focus, held]);
  const focusFirst = useMemo(() => (held ? segmentsSentenceCount(segments, focusTurn - 1) : 0), [segments, focusTurn, held]);

  // The turn the scroller keeps in view: the focused one while a focus is
  // held, else the playhead's. Scoped to the panel's own scroller (rather
  // than scrollIntoView, which would also scroll every ancestor and take the
  // page out from under the canvas). Keyed on that turn ALONE: new turns
  // append below the active one, so its offset doesn't move, and firing on
  // arrivals would yank the panel back while you're reading history — and
  // while a focus is held the playhead's turn is not a key at all, so the
  // live end never pulls the panel off what was asked for.
  const scrollTurn = held ? focusTurn : currentTurn;
  useEffect(() => {
    const el = targetRef.current;
    const scroller = scrollerRef.current;
    if (!el || !scroller) return;
    scroller.scrollTo({
      top: el.offsetTop - scroller.offsetTop - scroller.clientHeight / 3,
      behavior: "smooth",
    });
  }, [scrollTurn, focus]);

  // The focused turn, sentence by sentence, with the asked-for one marked.
  const markedTurn = (segment) =>
    (segment.sentences || []).map((sentence, i) => (
      <Fragment key={i}>
        {i ? " " : ""}
        {focusFirst + i === focus ? <mark className={s.hit}>{sentenceText(sentence)}</mark> : sentenceText(sentence)}
      </Fragment>
    ));

  return (
    <Box className={s.panel}>
      {editorState && symbolName && (
        <SlideDown title={"Primary Symbol: " + toTitleCase(symbolName)}>
          <Editor editorState={editorState} readOnly={true} />
        </SlideDown>
      )}
      <Box className={s.headingRow}>
        <Typography className={s.heading}>Transcript</Typography>
        {held && (
          <Button size="small" sx={{ textTransform: "none", fontSize: 12, px: 1, minWidth: 0 }} onClick={onRelease}>
            Back to live
          </Button>
        )}
      </Box>
      <Box className={s.transcript} ref={scrollerRef}>
        {segments.length ? (
          segments.map((segment, idx) => {
            const active = idx === currentTurn;
            const focused = held && idx === focusTurn;
            return (
              <Box
                key={segmentSeq(segment, idx)}
                ref={idx === scrollTurn ? targetRef : null}
                className={active ? s.turnActive : s.turn}>
                {(segment.speaker || segment.start) && (
                  <Box className={s.turnMeta}>
                    {segment.speaker && <strong>{segment.speaker}</strong>}
                    {segment.start && <span>{segment.start}</span>}
                  </Box>
                )}
                <p>{focused ? markedTurn(segment) : turnTexts[idx]}</p>
              </Box>
            );
          })
        ) : (
          <Box className={s.waiting}>Waiting for transcript</Box>
        )}
      </Box>
    </Box>
  );
}

LiveTapInfoPanel.propTypes = {
  // The painted background's primary symbol, by its catalog name (shown only).
  symbolName: PropTypes.string,
  description: PropTypes.string,
  segments: PropTypes.array,
  currentTurn: PropTypes.number,
  // A sentence the host asked to see, by its ordinal in the transcript.
  // Null: the panel follows the playhead.
  focus: PropTypes.number,
  onRelease: PropTypes.func,
};

// The live page re-renders twice per poll (useLiveTranscript toggles `refreshing`)
// even when the feed is unchanged; the panel's props only change when the feed or
// the playhead actually moves, so memoizing skips the transcript list on those.
export default memo(LiveTapInfoPanel);
